// src/utils/taxDefaults.js
// Default GST/VAT rates for expense categories, keyed by office location.
//
// Source of truth for daily expense entry: when a user enters a gross amount
// without a tax breakdown, the system looks up the (category, office) pair
// here and stamps an estimated tax split on the expense. The bookkeeper later
// confirms or reclassifies items in TaxReconciliationPage.
//
// Categories match the canonical list in src/utils/expenseShared.js
// (PROJECT_EXPENSE_CATEGORIES ∪ OFFICE_OVERHEAD_CATEGORIES).
//
// Rate semantics:
//   rate         — fraction of *net* (e.g. 0.21 for NL 21% VAT). Gross-up uses
//                  net = gross / (1 + rate).
//   recoverable  — whether the input tax can be reclaimed against output tax.
//                  Some categories (Meals & Entertainment, Insurance, Salary)
//                  are non-recoverable by statute even when tax was paid.
//   code         — the tax code stamped on the expense row. Stable identifier
//                  used by the reconciliation page and the tax_codes lookup.
//
// References (rates current as of FY 2026):
//   NL VAT:  21% standard, 9% reduced (food, books, hotels, transport),
//            0% / exempt (insurance, education, healthcare, financial services).
//   IN GST: 5%, 12%, 18%, 28% slabs. Most services 18%. Restaurants 5% (no ITC).
//           Section 17(5) blocks ITC on food, club memberships, motor vehicles.

import { normalizeOfficeLocation } from './expenseShared';

// ── Tax codes ──────────────────────────────────────────────────────────
// Stable string identifiers; the same set is seeded into the tax_codes table
// by the migration so that joins/reports can reference the same keys.

export const TAX_CODES = {
  // Netherlands
  'NL-VAT-21':       { country: 'NL', rate: 0.21, recoverable: true,  label: 'NL VAT 21% (standard)' },
  'NL-VAT-9':        { country: 'NL', rate: 0.09, recoverable: true,  label: 'NL VAT 9% (reduced)' },
  'NL-VAT-21-NR':    { country: 'NL', rate: 0.21, recoverable: false, label: 'NL VAT 21% (non-recoverable)' },
  'NL-VAT-9-NR':     { country: 'NL', rate: 0.09, recoverable: false, label: 'NL VAT 9% (non-recoverable)' },
  'NL-VAT-0':        { country: 'NL', rate: 0.00, recoverable: true,  label: 'NL VAT 0% / zero-rated' },
  'NL-EXEMPT':       { country: 'NL', rate: 0.00, recoverable: false, label: 'NL exempt (out of scope)' },
  'NL-RC':           { country: 'NL', rate: 0.21, recoverable: true,  label: 'NL reverse charge 21%' },

  // India
  'IN-GST-18':       { country: 'IN', rate: 0.18, recoverable: true,  label: 'IN GST 18%' },
  'IN-GST-12':       { country: 'IN', rate: 0.12, recoverable: true,  label: 'IN GST 12%' },
  'IN-GST-5':        { country: 'IN', rate: 0.05, recoverable: true,  label: 'IN GST 5%' },
  'IN-GST-5-NR':     { country: 'IN', rate: 0.05, recoverable: false, label: 'IN GST 5% (no ITC — restaurants)' },
  'IN-GST-18-NR':    { country: 'IN', rate: 0.18, recoverable: false, label: 'IN GST 18% (no ITC — s.17(5))' },
  'IN-EXEMPT':       { country: 'IN', rate: 0.00, recoverable: false, label: 'IN exempt / out of scope' },
  'IN-RCM-18':       { country: 'IN', rate: 0.18, recoverable: true,  label: 'IN RCM 18% (import of services)' },
};

// ── Default matrix: category × office → tax code ───────────────────────
// Bookkeeper override is expected; this is the starting estimate only.
// Categories without a row default to the office's standard rate.

export const DEFAULT_TAX_BY_CATEGORY = {
  // Daily-life office costs
  'Energy & Utilities':                                    { NL: 'NL-VAT-21', IN: 'IN-GST-18' },
  'Equipment & IT: software, hardware':                    { NL: 'NL-VAT-21', IN: 'IN-GST-18' },
  'Fixed Assets Additions':                                { NL: 'NL-VAT-21', IN: 'IN-GST-18' },
  'Office Operations: supplies, courier, subscriptions':   { NL: 'NL-VAT-21', IN: 'IN-GST-18' },
  'Premises':                                              { NL: 'NL-VAT-21', IN: 'IN-GST-18' },

  // Professional / project work
  'Professional Services':                                 { NL: 'NL-VAT-21', IN: 'IN-GST-18' },
  'Materials':                                             { NL: 'NL-VAT-21', IN: 'IN-GST-18' },
  'Marketing':                                             { NL: 'NL-VAT-21', IN: 'IN-GST-18' },

  // Travel
  'Travel Costs':                                          { NL: 'NL-VAT-9',  IN: 'IN-GST-5'  },

  // Statutorily non-recoverable
  'Meals & Entertainment':                                 { NL: 'NL-VAT-9-NR', IN: 'IN-GST-5-NR' },

  // Out-of-scope / exempt
  'Insurance':                                             { NL: 'NL-EXEMPT', IN: 'IN-EXEMPT' },
  'Finance':                                               { NL: 'NL-VAT-21', IN: 'IN-GST-18' },
  'HR':                                                    { NL: 'NL-VAT-21', IN: 'IN-GST-18' },
  'Pension':                                               { NL: 'NL-EXEMPT', IN: 'IN-EXEMPT' },
  'Salary':                                                { NL: 'NL-EXEMPT', IN: 'IN-EXEMPT' },
  'Salary Tax':                                            { NL: 'NL-EXEMPT', IN: 'IN-EXEMPT' },

  // Catch-all
  'Miscellaneous':                                         { NL: 'NL-VAT-21', IN: 'IN-GST-18' },
};

// ── Status labels ──────────────────────────────────────────────────────
export const TAX_STATUS_LABELS = {
  estimated:    'Estimated',
  confirmed:    'Confirmed',
  reclassified: 'Reclassified',
  none:         'No tax data',
};

// Categories where a foreign vendor triggers reverse charge instead of the
// standard domestic rate. Cross-border B2B services in NL → reverse charge
// 21%; import of services in IN → RCM 18%. Goods imports follow customs/IGST,
// not RCM, so we keep those out — Materials stays at standard rate.
export const FOREIGN_VENDOR_RC_CATEGORIES = new Set([
  'Equipment & IT: software, hardware',
  'Marketing',
  'Professional Services',
]);

const FOREIGN_VENDOR_CODES = { NL: 'NL-RC', IN: 'IN-RCM-18' };

// ── Lookup ─────────────────────────────────────────────────────────────
// Returns { code, rate, recoverable, label } or null if no rule applies.
//
// isForeignVendor: when true, categories in FOREIGN_VENDOR_RC_CATEGORIES
// resolve to the reverse-charge code for the office. Defaults to false so
// the matrix lookup matches the daily-entry path that doesn't (yet) know
// vendor origin — the bookkeeper toggles this on the expense form or
// flips the row's tax code at reconciliation.
export function getDefaultTaxForCategory(category, officeLocation, isForeignVendor = false) {
  const office = normalizeOfficeLocation(officeLocation);
  let code;
  if (isForeignVendor && FOREIGN_VENDOR_RC_CATEGORIES.has(category)) {
    code = FOREIGN_VENDOR_CODES[office];
  } else {
    const row = DEFAULT_TAX_BY_CATEGORY[category];
    code = row?.[office] || (office === 'NL' ? 'NL-VAT-21' : 'IN-GST-18');
  }
  const def = TAX_CODES[code];
  if (!def) return null;
  return {
    code,
    rate: def.rate,
    recoverable: def.recoverable,
    label: def.label,
  };
}

// ── Gross-up helper ────────────────────────────────────────────────────
// Returns { net, tax } given a gross amount and a rate.
export function splitGrossByRate(gross, rate) {
  const g = Number(gross || 0);
  const r = Number(rate || 0);
  if (!isFinite(g) || !isFinite(r) || r <= 0) return { net: g, tax: 0 };
  const net = +(g / (1 + r)).toFixed(2);
  return { net, tax: +(g - net).toFixed(2) };
}
